import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TaxRate,
  TaxRateExpenseCurrency,
  TaxRatePayload,
  createTaxRate,
  deleteTaxRate,
  getTaxRates,
  updateTaxRate,
} from '../services/api';
import { SortState, compareValues, formatDateShort, toggleSort } from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import '../styles/DataPage.css';
import './TaxesPage.css';

type TaxRateSortKey = 'expense_currency' | 'start_date' | 'end_date' | 'tax_rate';

type TaxesFilterMenuKey = 'expense_currency' | null;

const EXPENSE_CURRENCY_OPTIONS: TaxRateExpenseCurrency[] = ['бн', 'ип', 'нал'];

interface TaxRateFormState {
  expense_currency: TaxRateExpenseCurrency;
  start_date: string;
  end_date: string;
  tax_rate: string;
}

const EMPTY_FORM: TaxRateFormState = {
  expense_currency: 'бн',
  start_date: '',
  end_date: '',
  tax_rate: '',
};

function displayEndDate(value: string | null | undefined): string {
  return value ? formatDateShort(value) : '—';
}

function formatTaxRate(value: number): string {
  return `${new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)} %`;
}

function dateInRange(value: string | undefined | null, from: string, to: string): boolean {
  if (!value?.trim()) {
    return !from.trim() && !to.trim();
  }
  const hasFrom = Boolean(from.trim());
  const hasTo = Boolean(to.trim());
  if (!hasFrom && !hasTo) {
    return true;
  }
  const ts = new Date(value.slice(0, 10)).getTime();
  if (Number.isNaN(ts)) {
    return true;
  }
  const fromTs = hasFrom ? new Date(`${from}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? new Date(`${to}T23:59:59`).getTime() : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

function parseRateBound(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) {
    return null;
  }
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

function matchesSearch(row: TaxRate, query: string): boolean {
  if (!query.trim()) {
    return true;
  }
  const haystack = [
    row.expense_currency,
    row.start_date,
    row.end_date,
    formatTaxRate(row.tax_rate),
    String(row.tax_rate),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

const TaxesPage: React.FC = () => {
  const { can } = useAuth();
  const canView = can('MASTERDATA', 'EDIT');
  const canCreate = can('MASTERDATA', 'CREATE');
  const canEdit = can('MASTERDATA', 'EDIT');
  const canDelete = can('MASTERDATA', 'DELETE');

  const [rows, setRows] = useState<TaxRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [formData, setFormData] = useState<TaxRateFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<TaxesFilterMenuKey>(null);
  const [filterCurrencies, setFilterCurrencies] = useState<string[]>([]);
  const [filterStartFrom, setFilterStartFrom] = useState('');
  const [filterStartTo, setFilterStartTo] = useState('');
  const [filterEndFrom, setFilterEndFrom] = useState('');
  const [filterEndTo, setFilterEndTo] = useState('');
  const [minRateFilter, setMinRateFilter] = useState('');
  const [maxRateFilter, setMaxRateFilter] = useState('');
  const [sortState, setSortState] = useState<SortState<TaxRateSortKey>>({
    key: 'start_date',
    direction: 'desc',
  });

  const load = useCallback(async (): Promise<void> => {
    if (!canView) {
      setLoading(false);
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await getTaxRates();
      setRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const selectedRow = useMemo(
    () => (selectedId == null ? null : rows.find((row) => row.id === selectedId) ?? null),
    [rows, selectedId],
  );

  useEffect(() => {
    if (selectedId != null && !rows.some((row) => row.id === selectedId)) {
      setSelectedId(null);
    }
  }, [rows, selectedId]);

  const currencyFilterOptions = useMemo(
    () => [...EXPENSE_CURRENCY_OPTIONS],
    [],
  );

  const clearFilters = (): void => {
    setFilterCurrencies([]);
    setFilterStartFrom('');
    setFilterStartTo('');
    setFilterEndFrom('');
    setFilterEndTo('');
    setMinRateFilter('');
    setMaxRateFilter('');
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterCurrencies.length > 0 ||
    filterStartFrom.trim() !== '' ||
    filterStartTo.trim() !== '' ||
    filterEndFrom.trim() !== '' ||
    filterEndTo.trim() !== '' ||
    minRateFilter.trim() !== '' ||
    maxRateFilter.trim() !== '';

  const filteredRows = useMemo(() => {
    const minRate = parseRateBound(minRateFilter);
    const maxRate = parseRateBound(maxRateFilter);

    return rows.filter((row) => {
      const matchesCurrency =
        filterCurrencies.length === 0 || filterCurrencies.includes(row.expense_currency);
      const rate = Number(row.tax_rate) || 0;

      return (
        matchesSearch(row, searchText) &&
        matchesCurrency &&
        dateInRange(row.start_date, filterStartFrom, filterStartTo) &&
        dateInRange(row.end_date, filterEndFrom, filterEndTo) &&
        (minRate === null || rate >= minRate) &&
        (maxRate === null || rate <= maxRate)
      );
    });
  }, [
    rows,
    searchText,
    filterCurrencies,
    filterStartFrom,
    filterStartTo,
    filterEndFrom,
    filterEndTo,
    minRateFilter,
    maxRateFilter,
  ]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((left, right) => {
      const key = sortState.key;
      const leftValue = key === 'end_date'
        ? (left.end_date ?? '')
        : left[key];
      const rightValue = key === 'end_date'
        ? (right.end_date ?? '')
        : right[key];
      return compareValues(leftValue, rightValue, sortState.direction);
    });
    return sorted;
  }, [filteredRows, sortState]);

  useEffect(() => {
    if (selectedId != null && !sortedRows.some((row) => row.id === selectedId)) {
      setSelectedId(null);
    }
  }, [sortedRows, selectedId]);

  const openCreateModal = (): void => {
    setFormData(EMPTY_FORM);
    setFormError('');
    setModalMode('create');
    setModalOpen(true);
  };

  const openEditModal = (): void => {
    if (!selectedRow) {
      return;
    }
    setFormData({
      expense_currency: selectedRow.expense_currency,
      start_date: selectedRow.start_date,
      end_date: selectedRow.end_date ?? '',
      tax_rate: String(selectedRow.tax_rate),
    });
    setFormError('');
    setModalMode('edit');
    setModalOpen(true);
  };

  const closeModal = (): void => {
    if (saving) {
      return;
    }
    setModalOpen(false);
    setFormError('');
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!formData.start_date.trim()) {
      setFormError('Укажите дату начала');
      return;
    }
    if (modalMode === 'create' && !formData.end_date.trim()) {
      setFormError('Укажите дату окончания');
      return;
    }
    if (formData.end_date && formData.start_date && formData.end_date < formData.start_date) {
      setFormError('Дата окончания не может быть раньше даты начала');
      return;
    }
    const rate = parseRateBound(formData.tax_rate);
    if (rate === null || rate < -100 || rate > 100) {
      setFormError('Укажите ставку налога от -100 до 100');
      return;
    }

    setSaving(true);
    setFormError('');
    const payload: TaxRatePayload = {
      expense_currency: formData.expense_currency,
      start_date: formData.start_date,
      end_date: formData.end_date.trim() ? formData.end_date : null,
      tax_rate: rate,
    };

    try {
      if (modalMode === 'create') {
        const created = await createTaxRate({
          ...payload,
          end_date: formData.end_date.trim(),
        });
        setRows((prev) => [...prev, created]);
        setSelectedId(created.id);
      } else if (selectedRow) {
        const updated = await updateTaxRate(selectedRow.id, payload);
        setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      }
      setModalOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!selectedRow || isDeleting) {
      return;
    }
    const confirmed = window.confirm(
      `Удалить ставку налога ${selectedRow.expense_currency} `
      + `с ${formatDateShort(selectedRow.start_date)}?`,
    );
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    setError('');
    try {
      await deleteTaxRate(selectedRow.id);
      setRows((prev) => prev.filter((row) => row.id !== selectedRow.id));
      setSelectedId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка удаления');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSort = (key: TaxRateSortKey): void => {
    setSortState((current) => toggleSort(
      current,
      key,
      key === 'start_date' || key === 'end_date' ? 'desc' : 'asc',
    ));
  };

  return (
    <div className="data-page articles-page taxes-page">
      {!canView ? (
        <div className="empty-state">Нет доступа к мастерданным (требуется право MASTERDATA:EDIT).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Налоги</h1>
            <div className="page-header-row__actions">
              {canCreate ? (
                <button type="button" className="create-button" onClick={openCreateModal}>
                  Создать
                </button>
              ) : null}
              {canEdit && selectedRow ? (
                <button type="button" className="secondary-button" onClick={openEditModal}>
                  Редактировать
                </button>
              ) : null}
              {canDelete && selectedRow ? (
                <button
                  type="button"
                  className="delete-row-button"
                  onClick={() => void handleDelete()}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Удаление…' : 'Удалить'}
                </button>
              ) : null}
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message">{error}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Валюта расхода"
                      options={currencyFilterOptions}
                      selected={filterCurrencies}
                      onSelectedChange={setFilterCurrencies}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'expense_currency'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'expense_currency' : null)}
                    />
                  </div>
                  <label>
                    Дата начала с
                    <input
                      type="date"
                      value={filterStartFrom}
                      onChange={(e) => setFilterStartFrom(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата начала по
                    <input
                      type="date"
                      value={filterStartTo}
                      onChange={(e) => setFilterStartTo(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата окончания с
                    <input
                      type="date"
                      value={filterEndFrom}
                      onChange={(e) => setFilterEndFrom(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата окончания по
                    <input
                      type="date"
                      value={filterEndTo}
                      onChange={(e) => setFilterEndTo(e.target.value)}
                    />
                  </label>
                  <label>
                    Ставка от, %
                    <input
                      type="text"
                      inputMode="decimal"
                      value={minRateFilter}
                      onChange={(e) => setMinRateFilter(e.target.value)}
                    />
                  </label>
                  <label>
                    Ставка до, %
                    <input
                      type="text"
                      inputMode="decimal"
                      value={maxRateFilter}
                      onChange={(e) => setMaxRateFilter(e.target.value)}
                    />
                  </label>
                </div>
              </aside>
            )}

            <section className="table-content">
              {loading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table taxes-page__table">
                      <colgroup>
                        <col className="taxes-page__colgrid taxes-page__colgrid--currency" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--date" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--date" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--rate" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className="sortable"
                            onClick={() => handleSort('expense_currency')}
                            role="button"
                          >
                            Валюта расхода
                          </th>
                          <th
                            className="sortable"
                            onClick={() => handleSort('start_date')}
                            role="button"
                          >
                            Дата начала
                          </th>
                          <th
                            className="sortable"
                            onClick={() => handleSort('end_date')}
                            role="button"
                          >
                            Дата окончания
                          </th>
                          <th
                            className="sortable col-num"
                            onClick={() => handleSort('tax_rate')}
                            role="button"
                          >
                            Налог, %
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedRows.map((row) => (
                            <tr
                              key={row.id}
                              className={`clickable-row${selectedId === row.id ? ' data-table__row--selected' : ''}`}
                              onClick={() => setSelectedId(row.id)}
                            >
                              <td>{row.expense_currency}</td>
                              <td>{formatDateShort(row.start_date)}</td>
                              <td>{displayEndDate(row.end_date)}</td>
                              <td className="col-num">{formatTaxRate(row.tax_rate)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer taxes-page__table">
                      <colgroup>
                        <col className="taxes-page__colgrid taxes-page__colgrid--currency" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--date" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--date" />
                        <col className="taxes-page__colgrid taxes-page__colgrid--rate" />
                      </colgroup>
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedRows.length}</td>
                          <td />
                          <td />
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>

          {modalOpen ? (
            <div className="taxes-page__modal-overlay" role="presentation" onClick={closeModal}>
              <div
                className="taxes-page__modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="taxes-modal-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="taxes-modal-title">
                  {modalMode === 'create' ? 'Новая ставка налога' : 'Редактирование ставки налога'}
                </h2>
                <form className="taxes-page__form" onSubmit={(e) => void handleSubmit(e)}>
                  <label className="taxes-page__field">
                    <span>Валюта расхода *</span>
                    <select
                      value={formData.expense_currency}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        expense_currency: e.target.value as TaxRateExpenseCurrency,
                      }))}
                      disabled={saving}
                    >
                      {EXPENSE_CURRENCY_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="taxes-page__field">
                    <span>Дата начала *</span>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))}
                      disabled={saving}
                      required
                    />
                  </label>
                  <label className="taxes-page__field">
                    <span>Дата окончания{modalMode === 'create' ? ' *' : ''}</span>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        end_date: e.target.value,
                      }))}
                      disabled={saving}
                      required={modalMode === 'create'}
                    />
                  </label>
                  <label className="taxes-page__field">
                    <span>Налог, % *</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="taxes-page__rate-input"
                      value={formData.tax_rate}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        tax_rate: e.target.value,
                      }))}
                      disabled={saving}
                      required
                    />
                  </label>

                  {formError ? <div className="error-message">{formError}</div> : null}

                  <div className="taxes-page__modal-actions">
                    <button type="button" className="secondary-button" onClick={closeModal} disabled={saving}>
                      Отмена
                    </button>
                    <button type="submit" className="create-button" disabled={saving}>
                      {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default TaxesPage;
